/**
 * Optimizes Cloudinary URLs by adding transformation parameters.
 * Reduces image file sizes by 40-70% with automatic format and quality.
 *
 * @param url - Cloudinary image URL
 * @param width - Target width (default 256 for 128px display at 2x retina)
 * @returns Optimized URL with f_auto, q_auto, and width params
 */
export function optimizeCloudinaryUrl(url: string, width = 256): string {
  if (!url || !url.includes("res.cloudinary.com")) {
    return url;
  }
  // Transform: .../upload/v123/... → .../upload/f_auto,q_auto,w_256/v123/...
  return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width}/`);
}

/**
 * Generates a srcSet string for responsive Cloudinary images.
 * Use with Next.js Image component's loader or as standalone srcSet.
 *
 * @param url - Cloudinary image URL
 * @param widths - Array of widths to generate (default: mobile/tablet/desktop)
 * @returns srcSet string for responsive images
 */
export function getCloudinarySrcSet(
  url: string,
  widths: number[] = [320, 640, 800, 1200]
): string {
  if (!url || !url.includes("res.cloudinary.com")) {
    return url;
  }
  return widths
    .map((w) => `${optimizeCloudinaryUrl(url, w)} ${w}w`)
    .join(", ");
}

/**
 * Returns optimized width based on device type.
 * Mobile uses smaller images to save bandwidth.
 *
 * @param isMobile - Whether the device is mobile
 * @param mobileWidth - Width for mobile devices (default 320)
 * @param desktopWidth - Width for desktop devices (default 800)
 * @returns Appropriate width for the device
 */
export function getResponsiveWidth(
  isMobile: boolean,
  mobileWidth = 320,
  desktopWidth = 800
): number {
  return isMobile ? mobileWidth : desktopWidth;
}
